import {
  ContentBatchRow,
  ContentCategoriesResult,
  ContentCategoryRow,
  ContentEvalRow,
  ContentEvalVote,
  ContentFlagReason,
  ContentFlagResolutionAction,
  ContentFlagRow,
  ContentFlagStatus,
  ContentLintReport,
  ContentPlatform,
  ContentQuestionRow,
  ContentQuestionState,
  ContentDifficulty,
  ContentDifficultyMix,
} from "../../common/contentTypes";
import { post } from "./rest";

const root = "/content";

/**
 * `rest.post` only throws for 401/400/404, so a 500 — a platform outage, a
 * missing PLATFORM_CLIENT_ID, an unreachable tunnel — resolves with the
 * server's `{error, message}` body instead. Reading `.items` off that yields
 * `undefined` and the screen crashes on the next `.map` rather than telling
 * anyone what broke. Every content call goes through here so a failed request
 * always lands in the caller's catch block.
 */
async function contentPost<T>(path: string, params?: unknown): Promise<T> {
  const data = await post(path, params);

  if (data && typeof data === "object" && (data as { error?: unknown }).error) {
    const { error, message } = data as { error: string; message?: string };
    throw new Error(message || error);
  }

  return data as T;
}

// ── Questions ────────────────────────────────────────────────────────

export interface QuestionFilters {
  search?: string;
  categoryIds?: string[];
  difficulty?: ContentDifficulty;
  states?: ContentQuestionState[];
  batchId?: string;
  /** Exact ids — used when a screen holds only an id (lint rows, flags). */
  questionIds?: string[];
  limit?: number;
  offset?: number;
}

export async function listQuestions(
  params: QuestionFilters,
): Promise<{ items: ContentQuestionRow[]; total: number }> {
  return await contentPost(`${root}/questions/list`, params);
}

/**
 * The server caps `limit` at 100 and `questionIds` at 200 per call, so read in
 * chunks: Quality's "delete all" buckets and the Flagged queue both routinely
 * exceed that, and an over-sized request 400s rather than clamping.
 */
const QUESTIONS_BY_IDS_CHUNK = 100;

/** Convenience read for the screens that start from a question id. */
export async function questionsByIds(
  questionIds: string[],
): Promise<ContentQuestionRow[]> {
  if (questionIds.length === 0) {
    return [];
  }

  const chunks: string[][] = [];
  for (
    let start = 0;
    start < questionIds.length;
    start += QUESTIONS_BY_IDS_CHUNK
  ) {
    chunks.push(questionIds.slice(start, start + QUESTIONS_BY_IDS_CHUNK));
  }

  const pages = await Promise.all(
    chunks.map((ids) =>
      listQuestions({
        questionIds: ids,
        states: ["active", "draft", "deleted"],
        limit: ids.length,
      }),
    ),
  );

  return pages.flatMap((page) => page.items);
}

export interface QuestionWrite {
  title: string;
  categoryId: string;
  difficulty: ContentDifficulty;
  choices: { label: string; correct: boolean }[];
  hiddenOnPlatforms?: ContentPlatform[];
}

export async function createQuestion(
  params: QuestionWrite,
): Promise<ContentQuestionRow> {
  return await contentPost(`${root}/questions/create`, params);
}

export async function updateQuestion(
  params: Partial<QuestionWrite> & { questionId: string },
): Promise<ContentQuestionRow> {
  return await contentPost(`${root}/questions/update`, params);
}

/** The server's ceiling for one call; a whole-bucket cleanup exceeds it. */
const DELETE_CHUNK = 200;

/** Cleanup, no flag. Sequential so a mid-way failure reports what already went. */
export async function deleteQuestions(params: {
  questionIds: string[];
}): Promise<{ removed: number }> {
  const { questionIds } = params;
  let removed = 0;

  for (let start = 0; start < questionIds.length; start += DELETE_CHUNK) {
    const chunk = questionIds.slice(start, start + DELETE_CHUNK);
    try {
      const page = await contentPost<{ removed: number }>(
        `${root}/questions/delete`,
        { questionIds: chunk },
      );
      removed += page.removed;
    } catch (err) {
      throw new Error(
        `Removed ${removed} of ${questionIds.length} before failing: ${
          (err as Error).message
        }`,
      );
    }
  }

  return { removed };
}

export async function lintReport(
  maxChoiceLabelLength?: number,
): Promise<ContentLintReport> {
  return await contentPost(`${root}/questions/lint`, { maxChoiceLabelLength });
}

/** Removes and opens a flag for review. Chunked like the bare delete. */
export async function flagAndRemoveQuestions(params: {
  questionIds: string[];
  reason: ContentFlagReason;
  comment?: string;
  customer?: string;
}): Promise<{ removed: number }> {
  const { questionIds, ...capture } = params;
  let removed = 0;

  for (let start = 0; start < questionIds.length; start += DELETE_CHUNK) {
    const chunk = questionIds.slice(start, start + DELETE_CHUNK);
    try {
      const page = await contentPost<{ removed: number }>(
        `${root}/questions/flag-and-remove`,
        { questionIds: chunk, ...capture },
      );
      removed += page.removed;
    } catch (err) {
      throw new Error(
        `Flagged ${removed} of ${questionIds.length} before failing: ${
          (err as Error).message
        }`,
      );
    }
  }

  return { removed };
}

// ── Categories ───────────────────────────────────────────────────────

export async function listCategories(
  params: { includeDeleted?: boolean } = {},
): Promise<ContentCategoriesResult> {
  return await contentPost(`${root}/categories/list`, params);
}

export async function createCategory(params: {
  group: string;
  name: string;
  emoji?: string;
}): Promise<ContentCategoryRow> {
  return await contentPost(`${root}/categories/create`, params);
}

export async function updateCategory(params: {
  categoryId: string;
  group?: string;
  name?: string;
  emoji?: string;
  favorite?: boolean;
  /** 1-based position among favourites; the server renumbers the rest. */
  favoriteRank?: number;
  hidden?: boolean;
  hiddenOnPlatforms?: ContentPlatform[];
}): Promise<ContentCategoryRow> {
  return await contentPost(`${root}/categories/update`, params);
}

export async function mergeCategories(params: {
  sourceCategoryId: string;
  targetCategoryId: string;
}): Promise<{ moved: number }> {
  return await contentPost(`${root}/categories/merge`, params);
}

// ── Batches (read-only; generation lands with PS-5469) ────────────────

export async function listBatches(
  params: { state?: string } = {},
): Promise<{ items: ContentBatchRow[] }> {
  return await contentPost(`${root}/batches/list`, params);
}

export async function getBatch(batchId: string): Promise<ContentBatchRow> {
  return await contentPost(`${root}/batches/get`, { batchId });
}

/**
 * Fire-and-poll: the platform returns the batch already `running` and the work
 * happens in polly-manager, so the caller redirects and watches the row rather
 * than awaiting completion.
 */
export async function generateBatch(params: {
  name: string;
  prompt: string;
  requested: number;
  categoryIds?: string[];
  /** Whole percentages; the platform requires all three summing to 100. */
  difficultyMix?: ContentDifficultyMix;
}): Promise<ContentBatchRow> {
  return await contentPost(`${root}/batches/generate`, params);
}

/**
 * Repairs soft QA failures with the generator and returns how many the rubric
 * agreed were improved — which can be fewer than requested, since a repair that
 * does not actually help is discarded server-side.
 */
export async function fixQaWithAi(params: {
  questionIds: string[];
  maxChoiceLabelLength: number;
}): Promise<{ fixedCount: number }> {
  return await contentPost(`${root}/questions/fix-qa`, params);
}

/** Draft batches only; the platform refuses a published or live run. */
export async function deleteBatch(
  batchId: string,
): Promise<{ deletedCount: number }> {
  return await contentPost(`${root}/batches/delete`, { batchId });
}

/** Only valid for a failed or stalled run; the platform refuses a healthy one. */
export async function retryBatch(batchId: string): Promise<ContentBatchRow> {
  return await contentPost(`${root}/batches/retry`, { batchId });
}

export async function renameBatch(params: {
  batchId: string;
  name: string;
}): Promise<ContentBatchRow> {
  return await contentPost(`${root}/batches/rename`, params);
}

export async function setBatchVisibility(params: {
  batchId: string;
  hiddenOnPlatforms: ContentPlatform[];
}): Promise<ContentBatchRow> {
  return await contentPost(`${root}/batches/visibility`, params);
}

export async function reviewBatch(params: {
  batchId: string;
  questionIds: string[];
  reviewStatus: "approved" | "rejected";
}): Promise<{ reviewedCount: number }> {
  return await contentPost(`${root}/batches/review`, params);
}

/**
 * The server is the publish gate — it refuses while any question is still
 * pending, and that error is what the screen surfaces.
 */
export async function publishBatch(
  batchId: string,
): Promise<{ publishedCount: number }> {
  return await contentPost(`${root}/batches/publish`, { batchId });
}

// ── Flags ────────────────────────────────────────────────────────────

export async function listFlags(
  params: { status?: ContentFlagStatus } = {},
): Promise<{ items: ContentFlagRow[] }> {
  return await contentPost(`${root}/flags/list`, params);
}

/** Bulk backfill; the server contains per-row failures and reports them. */
export async function importFlags(params: {
  rows: {
    line?: number;
    reason: ContentFlagReason;
    questionId?: string;
    questionTitle?: string;
    comment?: string;
    customer?: string;
    /**
     * Marks a row the support export had already closed out; the server
     * dismisses it rather than adding it to the review queue.
     *
     * Required, not optional. Excess-property checking does not apply to the
     * variable the modal passes, so an undeclared field reached the wire by
     * luck — but merely declaring it optional would let a reshape omit it and
     * still compile, silently re-opening every historically-resolved flag.
     * Requiring the key is what makes that a type error.
     */
    status: "resolved" | undefined;
  }[];
}): Promise<{
  imported: number;
  failed: number;
  failures: { line?: number; error: string }[];
}> {
  return await contentPost(`${root}/flags/import`, params);
}

export async function resolveFlag(params: {
  flagId: string;
  action: ContentFlagResolutionAction;
  note?: string;
}): Promise<ContentFlagRow> {
  return await contentPost(`${root}/flags/resolve`, params);
}

// ── Evals ────────────────────────────────────────────────────────────

export async function listEvals(params: {
  questionIds: string[];
}): Promise<{ items: ContentEvalRow[] }> {
  return await contentPost(`${root}/evals/list`, params);
}

export async function upsertEval(params: {
  questionId: string;
  vote: ContentEvalVote;
  rating?: number;
  comment?: string;
  context?: "library" | "review";
}): Promise<ContentEvalRow> {
  return await contentPost(`${root}/evals/upsert`, params);
}
